import React, { createContext, useContext, useCallback, useEffect } from 'react'
import { flushSync } from 'react-dom'
import { useNavigate, NavigateOptions } from 'react-router-dom'
import { AppType } from '../utils/appType'
import { applyAppTheme } from '../utils/appTheme'
import { prefersReducedMotion } from '../utils/fx'

const AppContext = createContext<AppType>('sushi')

export const AppProvider: React.FC<{
  appType: AppType
  children: React.ReactNode
}> = ({ appType, children }) => {
  useEffect(() => {
    applyAppTheme(appType)
  }, [appType])

  return <AppContext.Provider value={appType}>{children}</AppContext.Provider>
}

export const useAppType = (): AppType => useContext(AppContext)

type ViewTransitionDocument = Document & {
  startViewTransition?: (cb: () => void) => unknown
}

export function withViewTransition(update: () => void) {
  const doc = document as ViewTransitionDocument
  if (!doc.startViewTransition || prefersReducedMotion()) {
    update()
    return
  }
  doc.startViewTransition(() => flushSync(update))
}

export const useAppNavigate = () => {
  const navigate = useNavigate()
  const appType = useAppType()

  return useCallback(
    (to: string | number, options?: NavigateOptions) => {
      withViewTransition(() => {
        if (typeof to === 'number') {
          navigate(to)
        } else if (to.startsWith('/')) {
          navigate(`/${appType}${to}`, options)
        } else {
          navigate(to, options)
        }
      })
    },
    [navigate, appType],
  )
}
